import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { ArrowLeft, ShieldCheck, Mail, Phone, MapPin } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PrivacyPolicyPage() {
  const [legal, setLegal] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/settings/legal`);
        setLegal(data);
      } catch (err) {
        console.error('Failed to load legal settings:', err);
        // fall back to placeholder defaults
        setLegal({
          effective_date: 'February 1, 2026',
          last_updated: 'February 1, 2026',
          grievance_officer_name: '',
          grievance_officer_email: '',
          grievance_officer_phone: '',
          postal_address: '',
          office_hours: 'Monday to Friday, 10:00 AM to 6:00 PM (IST)',
          contact_email: '',
          tips_email: ''
        });
      }
    })();
  }, []);

  // Display a value or a "[To be filled]" placeholder so the page still reads cleanly when empty
  const show = (val, placeholder = '[To be filled by Crime Newz]') => val && val.trim() ? val : placeholder;

  const effectiveDate = legal?.effective_date || 'February 1, 2026';
  const lastUpdated = legal?.last_updated || 'February 1, 2026';

  const Section = ({ id, title, children }) => (
    <section id={id} className="mt-10" data-testid={`privacy-section-${id}`}>
      <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1A2E] mb-4 tracking-tight">
        {title}
      </h2>
      <div className="text-[#1A1A2E] leading-[1.8] space-y-4 text-base">{children}</div>
    </section>
  );

  return (
    <div className="min-h-screen bg-[#FFF8F0]" data-testid="privacy-policy-page">
      <SEO
        title="Privacy Policy"
        description="Crime Newz privacy policy — how we collect, use, and protect your information in accordance with India's DPDP Act and IT Rules."
        path="/privacy-policy"
        type="website"
      />
      <Header />

      <article className="max-w-[900px] mx-auto px-6 py-12">
        <Link
          to="/"
          data-testid="privacy-back-link"
          className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A] hover:text-[#E63946] transition-colors mb-6"
        >
          <ArrowLeft size={14} />
          Back to Home
        </Link>

        <div className="flex items-center gap-3 mb-3">
          <ShieldCheck size={28} className="text-[#E63946]" />
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A]">LEGAL</span>
        </div>
        <h1 className="text-4xl sm:text-5xl tracking-tight leading-none font-black text-[#1A1A2E] mb-4">
          Privacy Policy
        </h1>
        <div className="text-sm text-[#525252] mb-2">
          <strong>Effective Date:</strong> {effectiveDate}
        </div>
        <div className="text-sm text-[#525252] mb-8">
          <strong>Last Updated:</strong> {lastUpdated}
        </div>

        <div className="bg-[#FEE2E2] border-l-4 border-[#E63946] p-4 text-sm leading-relaxed text-[#1A1A2E]" data-testid="privacy-intro-banner">
          <strong>Crime Newz</strong> ("we", "us", "our", or the "Website") respects your privacy and is committed
          to protecting the personal data of our readers, contributors, and tipsters. This Privacy Policy
          explains how we collect, use, store, share, and protect your information in compliance with the
          <em> Digital Personal Data Protection Act, 2023 ("DPDPA")</em>, the <em>Information Technology Act, 2000</em>,
          the <em>Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data
          or Information) Rules, 2011 ("SPDI Rules")</em>, and the <em>Information Technology (Intermediary Guidelines
          and Digital Media Ethics Code) Rules, 2021</em>.
        </div>

        <Section id="who-we-are" title="1. Who We Are">
          <p>
            Crime Newz is a digital news publication that publishes crime, investigative, and public-safety
            journalism. For the purposes of the DPDPA, we are the <strong>Data Fiduciary</strong> in respect of the
            personal data we collect from you, and you (the user of the Website) are the <strong>Data Principal</strong>.
          </p>
        </Section>

        <Section id="information-we-collect" title="2. Information We Collect">
          <p>We collect the following categories of information when you interact with the Website:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Information You Provide Directly:</strong> Name, email address, phone number, postal address,
              and any other details you submit when creating an account, subscribing to a newsletter, submitting a news
              tip, or contacting us.</li>
            <li><strong>News Tips and User Submissions:</strong> Photos, videos, audio, documents, or any content
              you voluntarily submit to our editorial team.</li>
            <li><strong>Automatically Collected Data:</strong> IP address, browser type, device type and OS, referring
              URL, pages visited, time spent, and similar log data collected through cookies and analytics tools.</li>
            <li><strong>Cookies and Similar Technologies:</strong> Small text files stored on your device used to
              improve site functionality, remember preferences, measure traffic, and serve relevant advertising.</li>
            <li><strong>Sensitive Personal Data:</strong> We do not knowingly collect financial information,
              passwords, biometric data, or other categories defined as "Sensitive Personal Data or Information"
              under the SPDI Rules, unless you explicitly provide them while submitting a news tip.</li>
          </ul>
        </Section>

        <Section id="purpose" title="3. Purpose and Lawful Basis of Processing">
          <p>Your personal data is processed for the following purposes:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Publishing news, articles, and journalistic content in the public interest;</li>
            <li>Verifying the authenticity of tips, sources, and user-submitted content;</li>
            <li>Responding to your queries, feedback, or grievances;</li>
            <li>Sending newsletters, alerts, or other communications you have opted into;</li>
            <li>Personalising content recommendations and improving the reader experience;</li>
            <li>Operating, securing, and improving the Website and detecting fraudulent or unlawful activity;</li>
            <li>Complying with applicable law, court orders, or regulatory directions.</li>
          </ul>
          <p>
            We process your personal data on the lawful basis of <strong>your consent</strong>, our
            <strong> legitimate journalistic interests</strong>, and where applicable, the <strong>legal exemptions</strong>
            available to media organisations under Section 17 of the DPDPA.
          </p>
        </Section>

        <Section id="how-we-use" title="4. How We Use Your Information">
          <p>
            We use the information collected to deliver content, send communications you have subscribed to,
            personalise your reading experience, prevent abuse and fraud, and comply with legal obligations.
            We do <strong>not</strong> sell your personal data to third parties.
          </p>
        </Section>

        <Section id="cookies-ads" title="5. Cookies, Analytics & Advertising">
          <p>
            The Website uses cookies and similar technologies to keep you signed in, remember your preferences,
            measure traffic, and serve advertisements. We may use the following third-party services that set their
            own cookies under their own privacy policies:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Google AdSense / Google Ads:</strong> serves personalised and non-personalised advertisements
              based on your browsing behaviour. You can opt out via{' '}
              <a className="text-[#E63946] underline" href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">
                Google Ads Settings
              </a>.</li>
            <li><strong>Web analytics (e.g., Google Analytics):</strong> measures usage and traffic patterns.</li>
            <li><strong>Embedded content</strong> from YouTube, Twitter/X, Facebook, Instagram and similar platforms
              may set their own cookies when you interact with embedded videos or social posts.</li>
          </ul>
          <p>
            You can disable cookies through your browser settings, though some parts of the Website may not function
            properly without them.
          </p>
        </Section>

        <Section id="sharing" title="6. Sharing of Personal Data">
          <p>We may share your personal data only in the following circumstances:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>With <strong>service providers</strong> (cloud hosting, email delivery, analytics, payment processors)
              who process data on our behalf under strict confidentiality obligations;</li>
            <li>With <strong>law enforcement or regulatory authorities</strong> when required by Indian law, valid court
              orders, or government directions;</li>
            <li>With your <strong>express consent</strong> to do so;</li>
            <li>In the event of a <strong>merger, acquisition, or asset sale</strong>, in which case any successor will
              be bound by this Privacy Policy.</li>
          </ul>
          <p>We do not transfer your personal data to any third party for marketing without your prior consent.</p>
        </Section>

        <Section id="retention" title="7. Data Retention">
          <p>
            We retain personal data only for as long as necessary to fulfil the purposes outlined in this Policy, to
            comply with legal obligations (including under Section 67C of the IT Act, 2000), or to resolve disputes.
            Once data is no longer required, it is securely deleted or anonymised.
          </p>
        </Section>

        <Section id="your-rights" title="8. Your Rights as a Data Principal (DPDPA, 2023)">
          <p>Under the DPDPA, you have the following rights, subject to applicable exemptions:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Right to Information:</strong> obtain a summary of the personal data being processed about you;</li>
            <li><strong>Right to Correction & Erasure:</strong> request correction, completion, updating, or erasure of
              your personal data;</li>
            <li><strong>Right of Grievance Redressal:</strong> approach our Grievance Officer (see Section 13) for any
              complaint regarding the processing of your personal data;</li>
            <li><strong>Right to Nominate:</strong> nominate another individual to exercise your rights in the event
              of your death or incapacity;</li>
            <li><strong>Right to Withdraw Consent:</strong> withdraw consent at any time. Withdrawal will not affect
              the lawfulness of processing already undertaken;</li>
            <li><strong>Right to Opt-Out</strong> of marketing communications using the unsubscribe link or by writing
              to us.</li>
          </ul>
          <p>To exercise any of these rights, write to our Grievance Officer with proof of identity.</p>
        </Section>

        <Section id="children" title="9. Children's Privacy">
          <p>
            The Website is not directed at children under the age of <strong>18 years</strong>. In line with Section 9
            of the DPDPA, we do not knowingly collect, process, or track personal data of children without verifiable
            parental or guardian consent. If you believe a child has provided personal data without such consent, please
            write to our Grievance Officer immediately and we will delete such data.
          </p>
        </Section>

        <Section id="security" title="10. Reasonable Security Practices">
          <p>
            In accordance with Rule 8 of the SPDI Rules, we implement reasonable security practices including HTTPS
            encryption in transit, bcrypt-hashed passwords, role-based access controls for administrators, secure
            cloud object storage, server-side validation, and periodic security review. While we strive to protect
            your data, no method of internet transmission or electronic storage is 100% secure, and we cannot
            guarantee absolute security.
          </p>
        </Section>

        <Section id="cross-border" title="11. Cross-Border Data Transfers">
          <p>
            Some of our service providers (such as cloud hosting and email delivery) may be located outside India.
            Where personal data is transferred outside India, we ensure adequate safeguards are in place and that
            transfers comply with the conditions notified by the Central Government under Section 16 of the DPDPA.
          </p>
        </Section>

        <Section id="links" title="12. Third-Party Links">
          <p>
            The Website may contain links to external websites, including embedded YouTube videos, social media
            embeds, and advertiser destinations. We are not responsible for the privacy practices or content of
            those external sites. Please review their privacy policies separately.
          </p>
        </Section>

        <Section id="grievance" title="13. Grievance Officer (Required under IT Rules, 2011 & 2021)">
          <p>
            In accordance with the Information Technology Act, 2000, IT Rules 2011, and IT (Intermediary Guidelines
            and Digital Media Ethics Code) Rules, 2021, the details of our Grievance Officer are as follows:
          </p>
          <div className="border border-[#E5E5E5] bg-white p-5 mt-4" data-testid="grievance-officer-block">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A] mb-3">GRIEVANCE OFFICER</div>
            <ul className="space-y-2 text-sm">
              <li><strong>Name:</strong> {show(legal?.grievance_officer_name)}</li>
              <li className="flex items-start gap-2">
                <Mail size={14} className="text-[#E63946] mt-1 flex-shrink-0" />
                <span><strong>Email:</strong> {show(legal?.grievance_officer_email, '[To be filled — official grievance email]')}</span>
              </li>
              <li className="flex items-start gap-2">
                <Phone size={14} className="text-[#E63946] mt-1 flex-shrink-0" />
                <span><strong>Phone:</strong> {show(legal?.grievance_officer_phone, '[To be filled — Indian contact number]')}</span>
              </li>
              <li className="flex items-start gap-2">
                <MapPin size={14} className="text-[#E63946] mt-1 flex-shrink-0" />
                <span><strong>Postal Address:</strong> {show(legal?.postal_address, '[To be filled — Indian office address]')}</span>
              </li>
              <li><strong>Office Hours:</strong> {legal?.office_hours || 'Monday to Friday, 10:00 AM to 6:00 PM (IST)'}</li>
            </ul>
            <p className="mt-4 text-sm text-[#525252]">
              We aim to acknowledge every grievance within <strong>24 hours</strong> and resolve it within <strong>15 days</strong>
              of receipt, in line with Rule 3(2) of the IT (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021.
            </p>
          </div>
        </Section>

        <Section id="changes" title="14. Changes to this Privacy Policy">
          <p>
            We may revise this Privacy Policy from time to time to reflect changes in law, our services, or our
            practices. The "Last Updated" date at the top will indicate when changes took effect. Material changes
            will be highlighted on the Website. Continued use of the Website after such changes constitutes your
            acceptance of the revised Policy.
          </p>
        </Section>

        <Section id="contact" title="15. Contact Us">
          <p>For any questions or concerns about this Privacy Policy, please contact:</p>
          <div className="mt-3 border border-[#E5E5E5] bg-white p-5">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A] mb-3">CRIME NEWZ</div>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <Mail size={14} className="text-[#E63946] mt-1 flex-shrink-0" />
                <span><strong>General queries:</strong> {show(legal?.contact_email, '[To be filled — contact email]')}</span>
              </li>
              <li className="flex items-start gap-2">
                <Mail size={14} className="text-[#E63946] mt-1 flex-shrink-0" />
                <span><strong>News tips:</strong> {show(legal?.tips_email, '[To be filled — tips email]')}</span>
              </li>
              <li className="flex items-start gap-2">
                <Mail size={14} className="text-[#E63946] mt-1 flex-shrink-0" />
                <span><strong>Privacy / Data requests:</strong> {show(legal?.grievance_officer_email, '[To be filled — grievance email]')}</span>
              </li>
            </ul>
          </div>
        </Section>

        <div className="mt-12 pt-6 border-t border-[#E5E5E5] text-xs text-[#8A8A8A] text-center">
          This Privacy Policy is provided in compliance with applicable Indian law and is intended to be read
          alongside our Terms of Use. Nothing in this Policy constitutes legal advice.
        </div>
      </article>

      <Footer />
    </div>
  );
}

export default PrivacyPolicyPage;
